import { useState, useEffect, useRef } from "react";
import { AmiraResources } from "@/models/AmiraResources.interface";
import { getUniqueResourceProperties } from "../../utils/index";

const PLACEHOLDER_ASSIGNMENT_TEXT = "all digital lessons";
const PLACEHOLDER_TUTORING_TEXT = "tutoring";
const PLACEHOLDER_PROVIDERS_TEXT = "all providers";

export function useAssignmentFilters(
  availableResources: AmiraResources[],
  isTutoring?: boolean
) {
  const [providerOptions, setProviderOptions] = useState<{ label: string; value: string; }[]>([]);
  const [assignmentTypeOptions, setAssignmentTypeOptions] = useState<{ label: string; value: string; }[]>([]);

  // Snapshot the initial available resources to prevent dynamic updates during modal session
  const initialAvailableResources = useRef<AmiraResources[]>([]);
  const optionsGenerated = useRef(false);

  useEffect(() => {
    // Take a snapshot only once when availableResources first becomes available
    if (availableResources.length > 0 && initialAvailableResources.current.length === 0) {
      initialAvailableResources.current = [...availableResources];
    }
  }, [availableResources]);

  useEffect(() => {
    // Generate options only from the initial snapshot, not dynamic updates
    if (initialAvailableResources.current.length > 0 && !optionsGenerated.current) {
      const uniqueProps = getUniqueResourceProperties(initialAvailableResources.current);

      const providerOptionsList = [
        { label: PLACEHOLDER_PROVIDERS_TEXT, value: PLACEHOLDER_PROVIDERS_TEXT },
        ...uniqueProps.providers.map(provider => ({
          label: typeof provider === 'string' ? provider : provider.label,
          value: typeof provider === 'string' ? provider : provider.value
        }))
      ];

      const assignmentTypeOptionsList = [
        { label: "everything", value: PLACEHOLDER_ASSIGNMENT_TEXT },
        ...uniqueProps.activityTypes.map(type => ({
          label: typeof type === 'string' ? type : type.label,
          value: typeof type === 'string' ? type : type.value
        }))
      ];

      setProviderOptions(providerOptionsList);
      setAssignmentTypeOptions(assignmentTypeOptionsList);
      optionsGenerated.current = true;
    } else if (availableResources.length === 0 && !optionsGenerated.current) {
      // Fallback if no resources available
      setProviderOptions([{ label: PLACEHOLDER_PROVIDERS_TEXT, value: PLACEHOLDER_PROVIDERS_TEXT }]);
      setAssignmentTypeOptions([{ label: "everything", value: PLACEHOLDER_ASSIGNMENT_TEXT }]);
      optionsGenerated.current = true;
    }
  }, [initialAvailableResources.current]);

  return {
    providerOptions,
    assignmentTypeOptions,
    PLACEHOLDER_ASSIGNMENT_TEXT,
    PLACEHOLDER_PROVIDERS_TEXT
  };
}